import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ComparisonTheme } from './comparisonVisualThemes';

const CAP_KEYS = ['agents', 'carrier', 'driver', 'routes', 'alerts', 'tower'] as const;
const CAP_ICONS: Record<(typeof CAP_KEYS)[number], string> = {
  agents: 'ri-robot-2-line',
  carrier: 'ri-truck-line',
  driver: 'ri-user-location-line',
  routes: 'ri-map-2-line',
  alerts: 'ri-notification-3-line',
  tower: 'ri-dashboard-3-line',
};

export default function ComparisonAICommandCentreSection({ theme }: { theme: ComparisonTheme }) {
  const { t } = useTranslation();

  return (
    <section
      className="py-16 lg:py-20 border-y border-slate-200/80"
      style={{
        background: `linear-gradient(180deg, rgba(${theme.primaryRgb}, 0.06) 0%, #fff 45%, #f8fafc 100%)`,
      }}
      aria-labelledby="ai-command-centre-heading"
    >
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center max-w-3xl mx-auto mb-12 lg:mb-14">
          <p
            className="text-[11px] font-bold uppercase tracking-[0.2em] mb-3"
            style={{ color: theme.primary }}
          >
            {t('comparison.aiCommand.eyebrow')}
          </p>
          <h2
            id="ai-command-centre-heading"
            className="text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight mb-4"
          >
            {t('comparison.aiCommand.title')}
          </h2>
          <p className="text-slate-600 leading-relaxed text-[15px] lg:text-base">{t('comparison.aiCommand.intro')}</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {CAP_KEYS.map((key) => (
            <div
              key={key}
              className="rounded-2xl border border-slate-200/90 bg-white p-5 lg:p-6 shadow-sm hover:shadow-md transition-shadow"
            >
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center text-xl text-white mb-4"
                style={{ backgroundColor: theme.primary }}
                aria-hidden
              >
                <i className={CAP_ICONS[key]} />
              </div>
              <h3 className="text-base font-semibold text-slate-900 mb-2">
                {t(`comparison.aiCommand.capabilities.${key}.title`)}
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                {t(`comparison.aiCommand.capabilities.${key}.body`)}
              </p>
            </div>
          ))}
        </div>
        <p className="text-center mt-10">
          <Link
            to="/features/ai-agents"
            className="inline-flex items-center gap-2 text-sm font-semibold hover:underline"
            style={{ color: theme.primary }}
          >
            {t('comparison.aiCommand.ctaFeatures')}
            <i className="ri-arrow-right-line" aria-hidden />
          </Link>
        </p>
      </div>
    </section>
  );
}
